import { NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { GenericDataGridComponent } from './components/generic-data-grid/generic-data-grid.component';
import { TrimFieldDirective } from './directives/trim-field.directive';
import { PinCurrencyPipe } from './pipes/pin-currency.pipe';
import { PinDatePipe } from './pipes/pin-date.pipe';
import { PinDecimalPipe } from './pipes/pin-decimal.pipe';
import { PinNotificationComponent } from './components/notification/notification.component';
import { InactivityDialogComponent } from './components/dialog/inactivity-dialog/inactivity-dialog.component';
import { PasswordInfoComponent } from './components/dialog/password-info/password-info.component';
import { LogoComponent } from './components/logo/logo.component';
import { DigitsOnlyDirective } from './directives/digits-only.directive';
import { AlphanumericCodeDirective } from './directives/alphanumeric-code.directive';
import { CardComponent } from './components/card/card.component';
import { CardFooterComponent } from './components/card/footer/card-footer.component';
import { CardTitleComponent } from './components/card/title/card-title.component';
import { CardContentComponent } from './components/card/content/card-content.component';
import { CardActionsComponent } from './components/card/actions/card-actions.component';
import { ConfirmDialogComponent } from './components/dialog/cancel-confirm/confirm-dialog.component';
import { PageComponent } from './components/page/page.component';
import { PageBackLinkComponent } from './components/page/back-link/back-link.component';
import { PageHeaderComponent } from './components/page/header/header.component';
import { PageTitleComponent } from './components/page/title/title.component';
import { PageDescriptionComponent } from './components/page/description/description.component';
import { PageContentComponent } from './components/page/content/content.component';
import { PageActionsComponent } from './components/page/actions/actions.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { DialogTitleComponent } from './components/dialog/title/title.component';
import { DialogContentComponent } from './components/dialog/content/content.component';
import { DialogActionsComponent } from './components/dialog/actions/actions.component';
import { AutoFocusDirective } from './directives/auto-focus.directive';
import { FeatureSwitchDirective } from './directives/feature-switch.directive';
import { PasswordInputComponent } from './components/password-input/password-input.component';
import { matSortHeaderProvider } from './components/generic-data-grid/mat-sort-header.service';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { LabeledValueComponent } from './components/labeled-value/labeled-value.component';
import { ButtonComponent } from './components/buttons/button.component';
import { NavTabsComponent } from './components/page/nav-tab/nav-tab.component';
import { DocumentsTitleComponent } from './components/documents-title/documents-title.component';
import { BankLogoComponent } from '../components/bank-logo/bank-logo.component';
import { AccountsListCardComponent } from './components/card/accounts-list-card/accounts-list-card.component';
import { BankFooterLogoComponent } from '../components/bank-footer-logo/bank-footer-logo.component';
import { ThreeDotsComponent } from '../components/three-dots/three-dots.component';
import { LoadingSpinnerWithTextMessageComponent } from '../components/loading-spinner-with-text-message/loading-spinner-with-text-message.component';
import { BadgeComponent } from './components/badge/badge.component';
import { InfoTooltipComponent } from './components/info-tooltip/info-tooltip.component';
import { NewHeaderComponent } from './components/new-header/new-header.component';
import { AccountsPaginatorComponent } from './components/accounts-paginator/accounts-paginator.component';
import { MenuComponent } from './components/menu/menu.component';
import { MenuItemsComponent } from './components/menu/menu-items/menu-items.component';
import { SkeletonCardComponent } from './components/card/accounts-list-skeleton-card/acccounts-list-skeleton-card.component';
import { IframeStatusInterceptor } from './interceptors/iframe-status-interceptor.service';

const ModuleImports = [
  CommonModule,
  RouterModule,

  // Material module component imports
  MatMenuModule,
  MatSortModule,
  MatTableModule,
  MatButtonModule,
  MatToolbarModule,
  MatIconModule,
  MatTabsModule,
  MatFormFieldModule,
  MatInputModule,
  MatSelectModule,
  MatCardModule,
  MatCheckboxModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatGridListModule,
  MatProgressSpinnerModule,
  MatDialogModule,
  MatPaginatorModule,
  MatExpansionModule,
  MatTooltipModule,
  MatDialogModule,
  MatDividerModule,
];

const ModuleDeclarations = [
  AccountsListCardComponent,
  AccountsPaginatorComponent,
  AutoFocusDirective,
  BadgeComponent,
  BankFooterLogoComponent,
  BankLogoComponent,
  ButtonComponent,
  CardActionsComponent,
  CardComponent,
  CardContentComponent,
  CardFooterComponent,
  CardTitleComponent,
  ConfirmDialogComponent,
  DialogActionsComponent,
  DialogComponent,
  DialogContentComponent,
  DialogTitleComponent,
  DigitsOnlyDirective,
  AlphanumericCodeDirective,
  DocumentsTitleComponent,
  FeatureSwitchDirective,
  FooterComponent,
  GenericDataGridComponent,
  HeaderComponent,
  InactivityDialogComponent,
  InfoTooltipComponent,
  LabeledValueComponent,
  LogoComponent,
  NavTabsComponent,
  NewHeaderComponent,
  PageActionsComponent,
  PageBackLinkComponent,
  PageComponent,
  PageContentComponent,
  PageDescriptionComponent,
  PageHeaderComponent,
  PageTitleComponent,
  PasswordInfoComponent,
  PasswordInputComponent,
  PinCurrencyPipe,
  PinDatePipe,
  PinDecimalPipe,
  PinNotificationComponent,
  SkeletonCardComponent,
  TooltipComponent,
  TrimFieldDirective,
  ThreeDotsComponent,
  LoadingSpinnerWithTextMessageComponent,
  MenuComponent,
  MenuItemsComponent
];

@NgModule({
    imports: [
        ModuleImports,
        ReactiveFormsModule,
        FormsModule,
        ...ModuleDeclarations
    ],
    providers: [
        CurrencyPipe,
        DatePipe,
        DecimalPipe,
        matSortHeaderProvider,
        { provide: HTTP_INTERCEPTORS, useClass: IframeStatusInterceptor, multi: true }
    ],
    exports:  [
      ...ModuleDeclarations,
      CommonModule,
      RouterModule,
      MatMenuModule,
      MatSortModule,
      MatTableModule,
      MatButtonModule,
      MatToolbarModule,
      MatIconModule,
      MatTabsModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatCardModule,
      MatCheckboxModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MatGridListModule,
      MatProgressSpinnerModule,
      MatDialogModule,
      MatPaginatorModule,
      MatExpansionModule,
      MatTooltipModule,
      MatDividerModule
    ]
})
export class SharedModule {}
